import { callIgnoreError } from '@/common/utilities';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

export class DaemonService {
  protected config: Record<string, AsyncDaemonConfig | TickDaemonConfig> = {};
  protected daemons: Record<string, DaemonState> = {};

  /**
   * All daemons have to be registered before st
   * @param config Daemon configuration.
   */
  register(config: AsyncDaemonConfig | TickDaemonConfig) {
    this.config[config.name] = config;
    this.daemons[config.name] = { isRunning: false };
    return this;
  }

  /**
   * Will remove any registered daemon. This is useful if you want to change the type of the
   * daemon. You have to first remove & and register it again.
   * Note: Any running daemon will be stopped.
   * @param name Name of the daemon.
   */
  remove(name: string) {
    this.stop(name);
    delete this.config[name];
    delete this.daemons[name];
    return this;
  }

  protected findDaemon(name: string): AsyncDaemonConfig | TickDaemonConfig {
    return this.config[name];
  }

  start(name: string) {
    const config = this.findDaemon(name);
    // Either config not found or daemon has already started.
    if (!config || this.daemons[name].isRunning) return Boolean(this.daemons[name]?.isRunning);

    const state = this.daemons[name] || { isRunning: true };
    this.daemons[name] = state;

    state.isRunning = true;
    this.nextDaemonTick(name);
    return state.isRunning;
  }

  stop(name: string) {
    if (!this.daemons[name]) return true;
    this.daemons[name].isRunning = false;
    this.daemons[name]?.clear?.();
    this.daemons[name].clear = null;
  }

  isDaemonRunning(name: string) {
    return Boolean(this.daemons[name]?.isRunning);
  }

  protected nextDaemonTick(name: string) {
    const config = this.findDaemon(name);
    if (!config || !this.isDaemonRunning(name)) return;
    return config.mode === 'async' ? this.asyncDaemonNextTick(name) : this.intervalDaemonNextTick(name);
  }

  protected getDaemonState(name: string) {
    const state = this.daemons[name] || {};
    this.daemons[name] = state;
    return state;
  }

  async asyncDaemonNextTick(name: string) {
    const state = this.getDaemonState(name);
    state.clear?.();

    if (!this.isDaemonRunning(name)) return;

    const [daemonProcess, stop] = this.createDaemonProcess();
    state.clear = stop;

    const config = this.findDaemon(name) as AsyncDaemonConfig;

    const CANCEL_ID = 'DAEMON_PROCESSED_CANCELED';

    const isAborted = new Promise<void>(daemonProcess.onClear).then(() => Promise.reject(CANCEL_ID));
    const runningExecutable = config.executable(daemonProcess);

    try {
      await Promise.race([runningExecutable, isAborted]);
    } catch (e) {
      e !== CANCEL_ID &&
        logger.error(
          `[DaemonService]: An async daemon (${name}) has thrown an unhandled error. Will stop execution to cause infinite loop.`,
          { error: parseErrorMessage(e) }
        );
      this.stop(name);
      return;
    }

    if (!this.isDaemonRunning(name)) return;
    this.nextDaemonTick(name);
  }

  intervalDaemonNextTick(name: string) {
    const state = this.getDaemonState(name);
    state.clear?.();

    if (!this.isDaemonRunning(name)) return;

    const [daemonProcess, stop] = this.createDaemonProcess();
    state.clear = stop;

    let nextTimeoutId: null | ReturnType<typeof setTimeout> = null;

    daemonProcess.onClear(() => nextTimeoutId && clearTimeout(nextTimeoutId));

    const config = this.findDaemon(name) as TickDaemonConfig;
    callIgnoreError(() => config.executable(daemonProcess));

    // After execution if this daemon has been stopped, then do not perform next tick.
    if (!this.isDaemonRunning(name)) return;
    nextTimeoutId = setTimeout(() => this.nextDaemonTick(name), config.frequency);
  }

  protected createDaemonProcess(): [DaemonProcess, VoidFunction] {
    const cleanups: VoidFunction[] = [];
    const process: DaemonProcess = {
      onClear: (fn) => cleanups.push(fn),
      isCleared: false,
    };
    const stopProcess = () => {
      process.isCleared = true;
      cleanups.forEach(callIgnoreError);
    };
    return [process, stopProcess];
  }

  get(name: string) {
    return this.findDaemon(name);
  }
}

export const daemonService = new DaemonService();

interface DaemonConfig<K extends string = string> {
  name: K;
}
export interface AsyncDaemonConfig<K extends string = string> extends DaemonConfig<K> {
  mode: 'async';
  executable: (process: DaemonProcess) => Promise<void>;
}
export interface TickDaemonConfig<K extends string = string> extends DaemonConfig<K> {
  mode: 'tick';
  executable: (process: DaemonProcess) => void;
  frequency: number;
}

type OnDaemonClear = (cleanUp: VoidFunction) => void;
export interface DaemonProcess {
  onClear: OnDaemonClear;
  isCleared: boolean;
}
interface DaemonState {
  isRunning: boolean;
  clear?: null | (() => void);
}
